/* eslint-disable @typescript-eslint/no-explicit-any */
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Popover, Card } from "antd";
import dayjs from "dayjs";
import PopOverCard from "../../../../Scheduling/Component/KanbanBoard/PopOverCard";

// --- Interfaces for Event Data ---
interface WorkOrder {
  id: string;
  name: string;
  duration: string; // e.g., "45m", "1.1h"
  status: "completed" | "pending"; // Added status for mock radio buttons
}

interface Task {
  id: string;
  name: string;
  progress?: string; // e.g., "1/32"
}

interface EventExtendedProps {
  serviceLine: string;
  visitTime: string;
  contract: string;
  workOrders?: WorkOrder[];
  tasks?: Task[];
  status?: string; // e.g., "Scheduled", "Completed"
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
}

interface FullCalendarEvent {
  id: string;
  title: string;
  start: string; // ISO 8601 string
  end?: string; // ISO 8601 string (optional for all-day events or if end isn't critical for display)
  extendedProps?: EventExtendedProps;
  // FullCalendar's built-in styling props
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
}

// --- Mock Data ---
const MOCK_EVENTS: FullCalendarEvent[] = [
  {
    id: "1",
    title: "Weekly service",
    start: "2025-07-02T16:00:00", // Weekly service on June 1st, 4pm
    end: "2025-07-02T17:00:00",
    backgroundColor: "#CCEECC",
    borderColor: "#66BB66",
    textColor: "#338833",
    extendedProps: {
      serviceLine: "Lawn care",
      visitTime: "4:00 PM - 5:00 PM",
      contract: "Weekly Service Contract 2025",
      status: "Scheduled",
    },
  },
  {
    id: "2",
    title: "Weekly service2",
    start: "2025-07-03T16:00:00", // Weekly service on June 1st, 4pm
    end: "2025-07-03T17:00:00",
    backgroundColor: "#d6e4ff",
    borderColor: "#4d88ff",
    textColor: "#0033cc",
    extendedProps: {
      serviceLine: "General Maintenance",
      visitTime: "4:00 PM - 5:00 PM",
      contract: "Standard Maintenance Agreement",
      status: "Completed",
    },
  },
  {
    id: "3",
    title: "Weekly service, aeration",
    start: "2025-07-04T16:00:00", // Weekly service on June 1st, 4pm
    end: "2025-07-04T17:00:00",
    backgroundColor: "#FFE0B2",
    borderColor: "#FFB74D",
    textColor: "#E65100",
    extendedProps: {
      serviceLine: "Aeration & Overseeding",
      visitTime: "1:00 PM - 4:00 PM",
      contract: "Fall Aeration Package",
      status: "Scheduled",
      workOrders: [
        { id: "wo1", name: "Mowing", duration: "45m", status: "completed" },
        {
          id: "wo2",
          name: "Warm Season Aeration",
          duration: "1.1h",
          status: "pending",
        },
      ],
      tasks: [
        { id: "task1", name: "Check irrigation system", progress: "1/32" },
        { id: "task2", name: "Customer follow-up call" },
      ],
    },
  },
  {
    id: "4",
    title: "Mowing",
    start: "2025-07-01T16:00:00", // Weekly service on June 1st, 4pm
    end: "2025-07-01T17:00:00",
    backgroundColor: "#FFECB3",
    borderColor: "#FFD700",
    textColor: "#FF8F00",
    extendedProps: {
      serviceLine: "Lawn Maintenance",
      visitTime: "9:00 AM - 10:00 AM",
      contract: "Seasonal Mowing",
      status: "Completed",
    },
  },
  {
    id: "5",
    title: "Irrigation Repair",
    start: "2025-07-06T10:30:00", // Aug 6th, 10:30am
    end: "2025-07-06T12:00:00",
    backgroundColor: "#BBDEFB",
    borderColor: "#64B5F6",
    textColor: "#1976D2",
    extendedProps: {
      serviceLine: "Irrigation",
      visitTime: "10:30 AM - 12:00 PM",
      contract: "Irrigation System Repair",
      status: "Scheduled",
    },
  },
  {
    id: "6",
    title: "Weekly service",
    start: "2025-07-06T13:00:00", // Aug 6th, 1pm
    end: "2025-07-06T14:00:00",
    backgroundColor: "#E1F5FE",
    borderColor: "#81D4FA",
    textColor: "#0288D1",
    extendedProps: {
      serviceLine: "Pest Control",
      visitTime: "1:00 PM - 2:00 PM",
      contract: "Quarterly Pest Control",
      status: "Scheduled",
    },
  },
  {
    id: "7",
    title: "Weekly service",
    start: "2025-07-13T13:00:00", // Aug 13th, 1pm
    end: "2025-07-13T14:00:00",
    backgroundColor: "#D6EAF8",
    borderColor: "#5DADE2",
    textColor: "#21618C",
    extendedProps: {
      serviceLine: "Pool Maintenance",
      visitTime: "1:00 PM - 2:00 PM",
      contract: "Monthly Pool Service",
      status: "Scheduled",
    },
  },
  {
    id: "8",
    title: "Fertilization",
    start: "2025-07-16T09:00:00", // Aug 15th, 9am
    end: "2025-07-16T10:30:00",
    backgroundColor: "#FBE9E7",
    borderColor: "#FFAB91",
    textColor: "#BF360C",
    extendedProps: {
      serviceLine: "Lawn Care",
      visitTime: "9:00 AM - 10:30 AM",
      contract: "Spring Fertilization",
      status: "Completed",
    },
  },
  {
    id: "9",
    title: "Mowing",
    start: "2025-07-17T11:00:00", // Aug 15th, 11am
    end: "2025-07-17T12:00:00",
    backgroundColor: "#F0F4C3",
    borderColor: "#C5E1A5",
    textColor: "#558B2F",
    extendedProps: {
      serviceLine: "Lawn Care",
      visitTime: "11:00 AM - 12:00 PM",
      contract: "Weekly Mowing",
      status: "Scheduled",
    },
  },
  {
    id: "10",
    title: "Pruning",
    start: "2025-07-15T13:00:00", // Aug 15th, 1pm
    end: "2025-07-15T14:15:00",
    backgroundColor: "#D1C4E9",
    borderColor: "#9575CD",
    textColor: "#4527A0",
    extendedProps: {
      serviceLine: "Landscaping",
      visitTime: "1:00 PM - 2:15 PM",
      contract: "Shrub Pruning",
      status: "Scheduled",
    },
  },
  {
    id: "11",
    title: "Pruning & Trimming",
    start: "2025-07-20T08:30:00", // Aug 20th, 8:30am
    end: "2025-07-20T10:30:00",
    backgroundColor: "#CCFF90",
    borderColor: "#9CCC65",
    textColor: "#558B2F",
    extendedProps: {
      serviceLine: "Landscaping",
      visitTime: "8:30 AM - 10:30 AM",
      contract: "Tree & Shrub Maintenance",
      status: "Completed",
    },
  },
  {
    id: "12",
    title: "Seasonal Cleanups",
    start: "2025-07-22T13:00:00", // Aug 22nd, 1pm
    end: "2025-07-22T15:00:00",
    backgroundColor: "#B2EBF2",
    borderColor: "#4DD0E1",
    textColor: "#00838F",
    extendedProps: {
      serviceLine: "Yard Maintenance",
      visitTime: "1:00 PM - 3:00 PM",
      contract: "Fall Cleanup Package",
      status: "Scheduled",
    },
  },
  {
    id: "13",
    title: "Weekly service",
    start: "2025-07-22T16:00:00", // Aug 22nd, 4pm
    end: "2025-07-22T17:00:00",
    backgroundColor: "#DCE775",
    borderColor: "#AED581",
    textColor: "#689F38",
    extendedProps: {
      serviceLine: "Lawn Care",
      visitTime: "4:00 PM - 5:00 PM",
      contract: "Weekly Service Contract 2025",
      status: "Scheduled",
    },
  },
  // Add more events as needed, adjusting dates for August 2025
];

// --- Main Calendar Component ---
export default function MonthlyCalendar() {
  // Custom event content rendering to style events
  const renderEventContent = (eventInfo: any) => {
    const { event } = eventInfo;
    // const { extendedProps } = event;
    // const eventStatus = extendedProps?.status || "Scheduled";

    // const getStatusColor = (status: string) => {
    //   switch (status) {
    //     case "Scheduled":
    //       return "#007bff"; // Blue
    //     case "Completed":
    //       return "#28a745"; // Green
    //     case "Cancelled":
    //       return "#dc3545"; // Red
    //     default:
    //       return "#6c757d"; // Gray
    //   }
    // };

    return (
      <div
        className="fc-event-main-frame"
        style={{
          backgroundColor: event.backgroundColor || "#A7D9A7",
          borderColor: event.borderColor || "#388E3C",
          color: event.textColor || "#333",
        }}
      >
        <Popover
          title={<PopOverCard visitData={event} />}
          placement="right"
          arrow={false}
        >
          <div className="fc-event-title-wrap">
            <div className="fc-event-title">{event.title}</div>
            {event.start && event.end && (
              <div
                style={{
                  fontSize: "0.8em",
                  fontWeight: "normal",
                  opacity: 0.8,
                }}
              >
                {dayjs(event.start).format("h:mm A")}
              </div>
            )}
          </div>
          {/* {eventStatus && (
            <Tag
              color={getStatusColor(eventStatus)}
              style={{
                position: "absolute",
                top: 2,
                right: 2,
                fontSize: "0.6em",
                lineHeight: "1.2",
                padding: "0px 4px",
                borderRadius: "2px",
              }}
            >
              {eventStatus}
            </Tag>
          )} */}
        </Popover>
      </div>
    );
  };

  return (
    <div className="profileTableWrapper">
      <Card className="profileTableCard">
        <div className="profile_calendar">
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            firstDay={1} // Start week on Monday
            headerToolbar={{
              left: "prev,title,next",
              center: "",
              right: "",
            }}
            events={MOCK_EVENTS}
            // eventClick={handleEventClick}
            eventContent={renderEventContent}
            height="auto"
            contentHeight="auto"
            aspectRatio={1.8}
            dayMaxEventRows={3}
          />
        </div>
      </Card>
    </div>
  );
}
